import { BasicLayout } from 'components/layouts/BasicLayout';
import { HEADER_NAV_MENU, HEADER_PAGES } from 'constants/headerNavMenu';
import { FC, ReactNode } from 'react';
import { FollowSearchResultsNavigation } from 'components/FollowSearchResultsNavigation';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { MetaDataSelector } from 'redux/features/jobList';
import { loginStatusSelector } from '@doda/common/src/redux/features/memberInfo';
import { memberRegistrationSuggestionBannerGroupNoSelector } from '@doda/common/src/redux/features/abTest';
import { useBannerVisible } from 'components/BottomBanner/BannerVisibilityContext';
import { RegistSuggestionBanner } from 'components/BottomBanner/RegistSuggestionBanner';
import styles from '../../screens/index.module.scss';
import { Breadcrumb } from '../Breadcrumb';
import { BreadCrumbLd } from '../BreadcrumbLd';
import { SearchSideBar } from '../SearchSideBar';
import { PageProps } from '../../index.page';
import { useMeasureAbTestForBanner } from '../../hooks/useMeasureAbTestForBanner';
import { shouldDisplayBottomBanner } from '../../logics/displayRegistSuggestionBanner';
import { ABTestPattern, MEMBER_REGIST_SERVICE_URL } from '../../constants';
import { measureAbTestForRegistSuggestionBanner } from '../../logics/analytics';
import { useAdjustScrollButton } from '../../hooks/useAdjustScrollTopButton';

/**
 * PageLayoutのProps
 * @param leadSentence ヘッダーリード文
 * @param sideBarProps サイドバー用Props
 * @param modalMasterData サイドバー用マスタデータ
 * @param breadCrumb パンくずリストデータ
 * @param children 画面の子要素
 */
interface Props {
  leadSentence: PageProps['leadSentence'];
  sideBarProps: PageProps['sideBarProps'];
  modalMasterData: PageProps['modalMasterData'];
  breadCrumb: PageProps['breadCrumb'];
  children: ReactNode;
}

export const PageLayout: FC<Props> = ({
  leadSentence,
  sideBarProps,
  modalMasterData,
  breadCrumb,
  children,
}) => {
  const loginStatus = useAppSelector(loginStatusSelector);
  const jobListMeta = useAppSelector(MetaDataSelector);

  const totalCount = jobListMeta?.totalCount ?? 0;
  const resultCount = jobListMeta?.resultCount ?? 0;
  const offset = jobListMeta?.offset ?? 0;

  const abTestGroupNo =
    useAppSelector(memberRegistrationSuggestionBannerGroupNoSelector) ?? '0';
  useMeasureAbTestForBanner(abTestGroupNo, 'visit');
  const isDisplayRegistSuggestionBanner = shouldDisplayBottomBanner(
    totalCount,
    abTestGroupNo
  );
  const showBanner = useBannerVisible();
  useAdjustScrollButton(isDisplayRegistSuggestionBanner && showBanner);

  const dispatch = useAppDispatch();
  const handleSignUpClick = () => {
    dispatch(
      measureAbTestForRegistSuggestionBanner(abTestGroupNo, 'registerClicked')
    );
  };
  const handleDisplayed = () => {
    dispatch(
      measureAbTestForRegistSuggestionBanner(abTestGroupNo, 'bannerDisplayed')
    );
  };
  return (
    <BasicLayout
      selectedNavItem={HEADER_NAV_MENU.SEARCH}
      leadSentence={leadSentence}
      currentPage={HEADER_PAGES.JOB_SEARCH_LIST}
    >
      {abTestGroupNo !== ABTestPattern.A && (
        <FollowSearchResultsNavigation
          loginStatus={loginStatus}
          totalCount={totalCount}
          resultCount={resultCount}
          offset={offset}
        />
      )}
      <Breadcrumb breadcrumbData={breadCrumb} />
      <BreadCrumbLd breadcrumbData={breadCrumb} />
      {isDisplayRegistSuggestionBanner && (
        <RegistSuggestionBanner
          buttonHref={MEMBER_REGIST_SERVICE_URL}
          onClick={handleSignUpClick}
          onDisplay={handleDisplayed}
        />
      )}
      <MainSection>
        <div className={styles.jobSearchList__sideBar}>
          <SearchSideBar
            sideBarProps={sideBarProps}
            modalMasterData={modalMasterData}
            isRegistSuggestionBannerDisplay={
              isDisplayRegistSuggestionBanner ? showBanner : false
            }
          />
        </div>
        <ContentsSection>{children}</ContentsSection>
      </MainSection>
    </BasicLayout>
  );
};

const MainSection: FC<{ children: ReactNode }> = ({ children }) => {
  return <div className={styles.jobSearchList__main}>{children}</div>;
};

const ContentsSection: FC<{ children: ReactNode }> = ({ children }) => {
  return <div className={styles.jobSearchList__contents}>{children}</div>;
};
